import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { IdInputStatusEnum } from '../../../models/irembo-id-input-status.enum';
import { FormArray, FormGroup } from '@angular/forms';
import { IEnvironment } from '../../../models/environment.model';

import {
  checkForValidFields,
  configureFields,
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import { v4 as uuidv4 } from 'uuid';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
import { IHttpPagedResponse } from '../../../../../public-api';

const INTEGRATION_URL = '/integration/v1/fetch/sync';

@Component({
  selector: 'irembogov-custom-tin-input',
  templateUrl: './custom-tin-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomTinInputComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  private environment: IEnvironment;
  private subscriptions = new Subscription();
  statusClass: string | undefined;

  fieldsToPopulate: Record<string, string>[] = [];

  tinLength = 9;
  useBaseUrl = true;
  isReadonly = false;
  usePrefetch = false;
  applicationBusinessId: string | undefined;

  constructor(
    @Inject('environment') environment: IEnvironment,
    private http: HttpClient,
    private formStateService: FormStateService,
    private cd: ChangeDetectorRef
  ) {
    super();
    this.environment = environment;
  }

  ngAfterViewInit(): void {
    if (this.field.props['tinLength'] !== undefined) {
      this.tinLength = this.field.props['tinLength'];
    }

    if (this.field.props['useBaseUrl'] !== undefined) {
      this.useBaseUrl = Boolean(this.field.props['useBaseUrl']);
    }

    if (this.field.props['usePrefetch']) {
      this.usePrefetch = Boolean(this.field.props['usePrefetch']);
    }

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        getPopulateReferenceForm(this.field, this.form)
      )
    );

    checkForValidFields(this.field);
    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    const formRef = getPopulateReferenceForm(this.field, this.form);

    if (this.fieldsToPopulate.length > 0) {
      this.subscriptions.add(
        configureFields(this.field, this.fieldsToPopulate, formRef)
      );
    }

    this.applicationBusinessId =
      this.formState[EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID];

    if (this.usePrefetch && this.applicationBusinessId) {
      this.isReadonly = true;
      this.getTINFromPrefetchUrl();
    } else {
      this.isReadonly = false;
    }

    this.clearTinDefaultValue();

    this.subscriptions.add(
      this.field.options?.fieldChanges?.subscribe(() => {
        this.clearTinDefaultValue();
      })
    );
  }

  clearTinDefaultValue(): void {
    if (this.field.defaultValue) {
      this.field.defaultValue = undefined;
    }
  }

  onVerifyTin(tin: string) {
    let { endpointCode } = this.field.props;

    const request_url = updateUrlWithApiGatewayBaseUrl(
      INTEGRATION_URL,
      this.environment
    );

    if (!endpointCode) {
      const defaultEndPointCode = this.environment.endpointCodes?.companyTin;
      if (!defaultEndPointCode) {
        throw new Error('Endpoint code is missing');
      }
      endpointCode = defaultEndPointCode;
    }

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload: {
        tinNumber: tin,
      },
      requester: uuidv4(),
    };

    this.statusClass = 'fetching';
    this.subscriptions.add(
      this.http.post<Record<string, unknown>>(request_url, params).subscribe({
        next: (res: Record<string, unknown>) => {
          this.statusClass = IdInputStatusEnum.SUCCESS;
          const data = res['data'] as Record<string, unknown>;
          const response = data['response'] as string;
          const resJson = JSON.parse(response);
          this.formStateService.saveFetchedDataKeyInFormState(
            this.field,
            resJson['ResponseObject']
          );
          if (
            this.fieldsToPopulate.length > 0 &&
            this.field.formControl.valid
          ) {
            const populateForm: FormGroup<any> | FormArray<any> =
              getPopulateReferenceForm(this.field, this.form);
            populateFormFields(
              resJson['ResponseObject'],
              this.field,
              populateForm
            );
          } else {
            this.field.options?.detectChanges?.(this.field);
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.statusClass = IdInputStatusEnum.DANGER;
          this.cd.detectChanges();
        },
      })
    );
  }

  getTINFromPrefetchUrl() {
    /**
     * No data fetch when in RFA mode and  not flagged in RFA
     */
    if (
      this.formStateService.checkIfApplicationInAnRFAState() &&
      !this.formStateService
        .getCurrentFormlyFormState()
        ?.[EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS]?.includes(
          <string>this.field.key
        )
    ) {
      return;
    }

    const url = updateUrlWithApiGatewayBaseUrl(
      '/application/v1/user/businesses',
      this.environment
    );
    this.statusClass = IdInputStatusEnum.VERIFYING;

    this.subscriptions.add(
      this.http.get<IHttpPagedResponse<any>>(url, { headers: {} }).subscribe({
        next: res => {
          const businesses = res.data.content;
          let business = null;
          if (businesses.length > 0) {
            business = businesses.find(
              (b: any) => b.guid === this.applicationBusinessId
            );

            if (business) {
              this.field.formControl.setValue(business.tinNumber);
              this.onVerifyTin(business.tinNumber);
              return;
            }
          }

          /**
           * Enable field without data fetch if in RFA mode and also flagged in RFA
           */
          if (this.formStateService.checkIfApplicationInAnRFAState()) {
            this.isReadonly = business?.tinNumber
              ? true
              : !this.formStateService
                  .getCurrentFormlyFormState()
                  ?.[EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS]?.includes(
                    <string>this.field.key
                  );

            this.statusClass = undefined;
            this.cd.detectChanges();
            return;
          }

          this.isReadonly = false;

          console.log('No business found');

          if (this.field.formControl.value) {
            this.statusClass = IdInputStatusEnum.DANGER;
          } else {
            this.statusClass = undefined;
          }
          this.cd.detectChanges();
        },
        error: () => {
          console.log('Error fetching businesses');

          this.statusClass = IdInputStatusEnum.DANGER;
          this.cd.detectChanges();
        },
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
